import { getRouteApi } from '@tanstack/react-router';
import { Label } from 'cpool-components/general/Label';
import { TextInput } from 'cpool-components/input/TextInput';
import { ALPHA_2_ISO_3166, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import { isValidPostalCode } from 'cpool-utils';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormField';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import type { SchemaLivingSituationForm } from '~/features/leadDetails/schemas/living.schema.ts';
import { canExecuteApplicantCommand } from '~/features/leadDetails/utils/aclPermissions';
import { GERMAN_POSTAL_CODE_ERROR_KEY } from '~/features/leadDetails/utils/germanPostalCode';
import { keepDigits } from '~/features/leadDetails/utils/keepDigits';

type Props = {
    antragsteller: 1 | 2;
    addressKind: 'anschrift' | 'vorherigeAnschrift';
};

const CityAndZip: React.FC<Props> = ({ addressKind, antragsteller }) => {
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { control, watch } = useFormContext<SchemaLivingSituationForm>();
    const land = watch(`antragsteller${antragsteller}.wohnsituation.${addressKind}.land`);
    const isGermany = land == null || land === ALPHA_2_ISO_3166.DE;
    const { t } = useTranslation();
    const isDisabled = !canExecuteApplicantCommand({
        applicant: antragsteller,
        commandName:
            addressKind === 'vorherigeAnschrift'
                ? LOAN_REQUEST_COMMANDS.changeApplicantPreviousAddress
                : LOAN_REQUEST_COMMANDS.changeApplicantAddress,
        permissions,
    });

    return (
        <div className="flex items-start gap-2.5">
            <Label className="h-8 w-[175px] shrink-0 justify-start">
                {t('ADDRESS.ZIP_CODE_SHORT')}
                {', '}
                {t('ADDRESS.LOCATION')}
            </Label>
            <div className="flex w-full min-w-0 items-start gap-2.5">
                <FormField<SchemaLivingSituationForm, string | null>
                    control={control}
                    name={`antragsteller${antragsteller}.wohnsituation.${addressKind}.plz`}
                >
                    {({ error, onChange, value }) => (
                        <TextInput
                            id={`antragsteller${antragsteller}.wohnsituation.${addressKind}.plz`}
                            className="w-[80px] shrink-0"
                            inputMode={isGermany ? 'numeric' : 'text'}
                            maxLength={isGermany ? 5 : undefined}
                            value={value ?? ''}
                            onChange={(event) =>
                                onChange((isGermany ? keepDigits(event.target.value, 5) : event.target.value) || null)
                            }
                            disabled={isDisabled}
                            error={isValidPostalCode(value, land) ? error : t(GERMAN_POSTAL_CODE_ERROR_KEY)}
                        />
                    )}
                </FormField>
                <FormField<SchemaLivingSituationForm, string | null>
                    control={control}
                    name={`antragsteller${antragsteller}.wohnsituation.${addressKind}.ort`}
                >
                    {({ error, onBlur, onChange, value }) => (
                        <TextInput
                            id={`antragsteller${antragsteller}.wohnsituation.${addressKind}.ort`}
                            className="min-w-0 flex-1"
                            value={value ?? ''}
                            onBlur={onBlur}
                            onChange={(event) => onChange(event.target.value)}
                            disabled={isDisabled}
                            error={error}
                        />
                    )}
                </FormField>
            </div>
        </div>
    );
};

export default CityAndZip;
